/**
 * staffRosterTimelineModel.ts
 * Pure helpers for the people×weeks ROSTER TIMELINE — the capacity hero of the
 * "Staff this job" view after Mike Billings' simplification (MIKE-SIMPLIFY.md
 * D2). Mike works in ONE surface: every person on the job, week by week, where
 * he sees the tasks he put them on (as faint context) and hand-sets ONE blanket
 * % per person·job·week (default 100, over-100 nags, never per-task, never
 * per-day).
 *
 * Two testable concerns, no React / MUI / theme:
 *   1. TASK CONTEXT — for each (person, week) the distinct tasks that person is
 *      on that week (from the assignment map), shown faintly behind the number
 *      so Mike can see "what am I setting a % against".
 *   2. CELL MODEL — the editable this-job % + the light TOTAL-load context (a
 *      person may be loaded by OTHER jobs), each classified into a utilization
 *      band so the cell can color the over-capacity nag.
 */

import { displayTaskName } from './taskDisplayName';
import { allocationBand, type AllocationBand } from '../../../../utils/scheduleAllocationBands';
import { buildCapacitySplit } from './capacitySplitModel';
import { addDaysISO, dayIndexInWeek } from '../../../../components/schedule/employeeScheduleShared';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

/** 'YYYY-MM-DD' regardless of any trailing time component the API may include. */
function isoDay(d: string | null | undefined): string {
  return (d ?? '').slice(0, 10);
}

/** A faint task chip shown as context behind a person's weekly %. */
export interface RosterTaskChip {
  taskKey: string;
  label: string;
}

/** Key a (person, week) pair for the task-context / cell maps. */
export function personWeekKey(userKey: string, weekStart: string): string {
  return `${userKey.toLowerCase()}::${isoDay(weekStart)}`;
}

/**
 * Build `${lk}::${weekStart}` → the distinct tasks that person is present on
 * that week (deduped by task, sorted by display label). Derived from the SAME
 * per-task presence rows the task cards use, so the timeline's context can never
 * drift from the cards. A person on no task that week has no entry (undefined).
 */
export function buildPersonWeekTasks(
  assignmentMap: Record<string, TaskAssignee[]>,
  tasks: readonly TaskWithBookings[],
): Map<string, RosterTaskChip[]> {
  const labelByKey = new Map<string, string>();
  for (const t of tasks) labelByKey.set(t.ScheduleTaskKey, displayTaskName(t.Name, t.TaskTypeName));

  const out = new Map<string, RosterTaskChip[]>();
  const seenByKey = new Map<string, Set<string>>();

  for (const [taskKey, rows] of Object.entries(assignmentMap)) {
    const label = labelByKey.get(taskKey) ?? taskKey;
    for (const r of rows) {
      const key = personWeekKey(r.UserKey, r.WeekStart);
      let seen = seenByKey.get(key);
      if (!seen) { seen = new Set(); seenByKey.set(key, seen); }
      if (seen.has(taskKey)) continue;
      seen.add(taskKey);
      const list = out.get(key) ?? [];
      list.push({ taskKey, label });
      out.set(key, list);
    }
  }

  for (const list of out.values()) list.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}

// ---------------------------------------------------------------------------
// Scenario-share day collection (open-row skyline, v1.1.47 morph)
// ---------------------------------------------------------------------------

/** The subset of an IndexAllocation the day collector needs. */
export interface ShareDaysAllocationInput {
  IndexAllocationKey: string;
  StartDate: string;
  EndDate: string;
}

/**
 * The days a person's draft share actually lands on within one week — feeds
 * the open row's 7-day skyline (`scenarioBarsFor`), ONE segment per run so
 * two sources in the same week never double-count the share.
 *
 * Union of:
 *   - their TASK assignment days that week (clipped to the week, Mon–Fri
 *     only — a range spanning a weekend runs the MACHINE, not the person);
 *   - their INDEX/EFFORT allocation windows that week (BUG 2026-07-30: a
 *     person staffed on a one-day index test — Atterberg on Aug 13 — used to
 *     fall through to the Mon–Fri fallback and paint ALL FIVE days booked,
 *     while the pinned "(index)" context line correctly showed one day).
 *
 * Mon–Fri fallback ONLY when the person has no task days AND no allocation
 * days that week (a roster-only share with nothing date-anchored to pin it).
 */
export function collectScenarioShareDays(args: {
  userKey: string;
  weekStart: string;
  tasks: readonly TaskWithBookings[];
  assignmentMap: Record<string, TaskAssignee[]>;
  indexAllocations: readonly ShareDaysAllocationInput[];
  indexAssigneesByAllocation: Record<string, Array<{ UserKey: string }>>;
}): string[] {
  const { userKey, weekStart } = args;
  const lk = userKey.toLowerCase();
  const weekEnd = addDaysISO(weekStart, 6);
  const days = new Set<string>();

  for (const t of args.tasks) {
    for (const a of args.assignmentMap[t.ScheduleTaskKey] ?? []) {
      if (a.UserKey.toLowerCase() !== lk || a.WeekStart !== weekStart) continue;
      const from = a.StartDate < weekStart ? weekStart : a.StartDate;
      const to = a.EndDate > weekEnd ? weekEnd : a.EndDate;
      for (let d = from; d <= to; d = addDaysISO(d, 1)) {
        if (dayIndexInWeek(d, weekStart) >= 2) days.add(d);
      }
    }
  }

  for (const alloc of args.indexAllocations) {
    if (alloc.EndDate < weekStart || alloc.StartDate > weekEnd) continue;
    const staffed = (args.indexAssigneesByAllocation[alloc.IndexAllocationKey] ?? [])
      .some((x) => x.UserKey.toLowerCase() === lk);
    if (!staffed) continue;
    const from = alloc.StartDate < weekStart ? weekStart : alloc.StartDate;
    const to = alloc.EndDate > weekEnd ? weekEnd : alloc.EndDate;
    for (let d = from; d <= to; d = addDaysISO(d, 1)) {
      if (dayIndexInWeek(d, weekStart) >= 2) days.add(d);
    }
  }

  if (days.size === 0) {
    for (let i = 2; i <= 6; i++) days.add(addDaysISO(weekStart, i)); // Mon–Fri fallback
  }
  return [...days].sort();
}

/** The resolved model of ONE (person, week) roster cell. */
export interface RosterCellModel {
  /** THIS job's hand-set blanket % for the week (the editable number). */
  thisJobPercent: number;
  /** Σ TOTAL weekly load across ALL jobs (undefined = stale / not fetched). */
  totalPercent: number | undefined;
  /** Load carried by OTHER jobs — max(0, total − this-job). */
  elsewherePercent: number;
  /** Band of the editable this-job % (drives its own over-100 red nag). */
  thisJobBand: AllocationBand;
  /** Band of the TOTAL load (the light cross-job over-capacity signal). */
  totalBand: AllocationBand;
  /** The faint task context for this cell (may be empty). */
  tasks: RosterTaskChip[];
  /**
   * SPLIT AVAILABILITY BAR segments (redesign 2026-07-14) — committed (firm,
   * live-schedule) + uncommitted (tentative, other draft plans); `thisJobPercent`
   * above is the third ("this project") segment. free/over are derived from
   * all three together (never from `totalPercent`, which is the legacy
   * day-count read and may disagree slightly with the D6 allocation split).
   */
  committedPercent: number;
  uncommittedPercent: number;
  freePercent: number;
  overPercent: number;
}

/**
 * Combine a person's this-job % + their total weekly load + their task context
 * into one cell model. Both percents are classified into utilization bands so
 * the cell can nag on over-100 (this-job) AND surface an over-capacity total
 * from other plans, without ever summing the two (count-once — the this-job %
 * is a component of the total, not additive to it).
 *
 * `committedPercent` / `uncommittedPercent` (from EmployeeWeekCell's D6+
 * split) feed the split availability bar; both default to 0 for back-compat
 * with a stale backend or a not-yet-fetched cell.
 */
export function buildRosterCellModel(args: {
  thisJobPercent: number;
  totalPercent: number | undefined;
  tasks?: RosterTaskChip[];
  committedPercent?: number;
  uncommittedPercent?: number;
}): RosterCellModel {
  const thisJobPercent = Math.round(args.thisJobPercent || 0);
  const total = typeof args.totalPercent === 'number' && Number.isFinite(args.totalPercent)
    ? Math.round(args.totalPercent)
    : undefined;
  const split = buildCapacitySplit(args.committedPercent ?? 0, args.uncommittedPercent ?? 0, thisJobPercent);
  return {
    thisJobPercent,
    totalPercent: total,
    elsewherePercent: typeof total === 'number' ? Math.max(0, total - thisJobPercent) : 0,
    thisJobBand: allocationBand(thisJobPercent),
    totalBand: allocationBand(total),
    tasks: args.tasks ?? [],
    committedPercent: split.committed,
    uncommittedPercent: split.uncommitted,
    freePercent: split.free,
    overPercent: split.over,
  };
}
